import moment from "moment";
import { TableCell, TDocumentDefinitions } from "pdfmake/interfaces";

import { generateFontSize, getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { HeaderLogoWithAddress } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

import { formatDate } from "react-lib/utils/dateUtils";

type Participant = {
  employee: number;
  employee_affiliation?: string | null;
  employee_name?: string | null;
  employee_position?: string | null;
};

type Course = {
  id: string;
  course_period: string;
  created: string;
  expiry_date: string;
  participants: Participant[];
  participants_count?: number;
  pricing: string;
  status: string;
  status_color: string;
  subject: string;
  training_type: string;
};

type FormTrainingBookingReportProps = {
  fields: Course[];
  params: {
    date_range: string;
    printed_date: string;
  };
};

const FormTrainingBookingReport = async (
  props: FormTrainingBookingReportProps
): Promise<TDocumentDefinitions> => {
  const font = getCompanyFont();
  const fontSizes = generateFontSize(font);

  const headerForm = await HeaderLogoWithAddress({
    font: getCompanyFont(),
    pageMargins: [0, 125, 0, 30],
    titleContent: [
      {
        alignment: "center",
        margin: [0, 5, 0, 0],
        style: { bold: true, fontSize: fontSizes[16] },
        text: "รายงานข้อมูลการจองการอบรม และผู้เข้าร่วม",
      },
      {
        alignment: "center",
        margin: [0, 0, 0, 10],
        style: { bold: true, fontSize: fontSizes[16] },
        text: `วันที่ ${props.params.date_range}`,
      },
    ],
  });

  const { images, lineHeights, styles } = headerForm;

  // Generate table rows
  const tableRows: TableCell[][] = [];

  for (const course of props.fields) {
    // Course row
    tableRows.push([
      {
        bold: true,
        rowSpan: course.participants.length,
        stack: [
          { alignment: "center", text: formatDate(moment(course.created)) },
          { color: course.status_color, relativePosition: { x: 2, y: 7.85 }, text: course.status },
        ],
      },
      { bold: true, text: course.id },
      { bold: true, text: course.subject },
      { alignment: "center", bold: true, text: course.training_type },
      { bold: true, text: course.course_period },
      { bold: true, text: course.pricing },
      { alignment: "center", bold: true, text: formatDate(moment(course.expiry_date)) },
      { alignment: "center", bold: true, text: String(course.participants_count) },
    ]);

    // Participant rows
    for (const participant of course.participants) {
      tableRows.push([
        {},
        { text: participant.employee },
        { text: participant.employee_name },
        { text: "" },
        { text: participant.employee_position },
        { text: participant.employee_affiliation },
        { text: "" },
        { text: "" },
      ]);
    }
  }

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[11.5],
      lineHeight: lineHeights[1],
    },
    pageSize: "A4",
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      footerData: {
        fontSize: fontSizes[10.5],
      },
      statusComplete: {
        color: "#22c55e",
      },
      tableHeader: {
        bold: true,
      },
    },
    content: [
      {
        margin: [15, 0, 15, 10],
        style: "tableStyle",
        table: {
          headerRows: 2,
          widths: ["10%", "12%", "22%", "8%", "12%", "15%", "10%", "11%"],
          body: [
            [
              {
                alignment: "center",
                style: "tableHeader",
                text: "Date",
              },
              {
                style: "tableHeader",
                text: "Course ID",
              },
              {
                style: "tableHeader",
                text: "Course Subject",
              },
              {
                style: "tableHeader",
                text: "Type",
              },
              {
                margin: [2, 0, 2, 0],
                noWrap: true,
                style: "tableHeader",
                text: "Period (hr)",
              },
              {
                style: "tableHeader",
                text: "Pricing",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Expiry date",
              },
              {
                alignment: "center",
                rowSpan: 2,
                style: "tableHeader",
                text: "No. of participant",
              },
            ],
            [
              {
                alignment: "center",
                style: "tableHeader",
                text: "",
              },
              { style: "tableHeader", text: "Employee ID" },
              { style: "tableHeader", text: "Employee" },
              { style: "tableHeader", text: "" },
              { margin: [2, 0, 2, 0], style: "tableHeader", text: "Position" },
              { style: "tableHeader", text: "Affiliate" },
              { style: "tableHeader", text: "" },
            ],
            ...tableRows,
          ],
        },
        layout: {
          hLineColor: () => "#cccccc",
          hLineWidth: (index, node) => {
            if (index === 0 || index === 2 || index === node.table.body.length) {
              return 0.5;
            }

            const currentRow: any[] = node.table.body[index] || [];

            return currentRow[0]?.stack ? 0.3 : 0;
          },
          paddingBottom: (index) => (index === 0 ? 2 : 4),
          paddingTop: (index) => (index === 1 ? 2 : 4),
          vLineWidth: () => 0,
        },
      },
    ],
    footer(currentPage: number, pageCount: number) {
      return {
        columns: [
          {
            alignment: "left",
            margin: [0, 5, 0, 0],
            style: "footerData",
            text: `วันที่พิมพ์/Printed date: ${props.params.printed_date}`,
          },
          { text: "", width: "*" },
          {
            alignment: "right",
            margin: [0, 5, 0, 0],
            style: "footerData",
            text: `หน้า ${currentPage} จาก ${pageCount}`,
          },
        ],
        margin: [15, 0, 15, 10],
      };
    },
  };
};

export default FormTrainingBookingReport;
